import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiCalendarCheck } from 'react-icons/bi'
import { getActTypes, deleteActType } from '../../actions/actTypes'
import Button from '../../components/Button'
import Table from '../../components/Table'
import { Checkbox } from '../../components/ModalInputs'
import Confirmation from '../../components/Confirmation/Confirmation'
import ActivityTypeModal from './Modal'
import styles from './styles.module.scss'

const TABLE_HEADERS = [
  { key: 'name', label: 'Наименование' },
  { key: 'closed', label: 'Закрыто', width: '100px' },
  { key: 'actions', label: '', width: '60px' }
]

const ActivityTypes = ({ data, setModalValue, closeSubModal }) => {
  const dispatch = useDispatch()
  const { list, loading } = useSelector(state => state.act_types)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [select, setSelect] = useState(null)
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)

  const isSelectionMode = data === 'eventList'

  useEffect(() => {
    dispatch(getActTypes())
  }, [dispatch])

  const handleDelete = useCallback((id) => (e) => {
    e.stopPropagation()
    setConfirmation(id)
  }, [])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleSelect = useCallback((item) => {
    if (isSelectionMode) {
      const isSelected = select?.acid === item.acid
      setSelect(isSelected ? null : item)
      if (!isSelected) {
        setModalValue({ acid: item.acid })
      }
    }
  }, [isSelectionMode, select, setModalValue])

  const handleRowClick = useCallback((item) => () => {
    handleSelect(item)
  }, [handleSelect])

  const handleRowDoubleClick = useCallback((item) => () => {
    openModal(item)()
  }, [openModal])

  const acceptConfirmation = useCallback(async () => {
    setIsDeleting(true)
    try {
      await dispatch(deleteActType(confirmation))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleConfirmSelection = useCallback(() => {
    closeSubModal()
  }, [closeSubModal])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!list) return []
    return [...list].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [list, sortField, sortAsc])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.acid

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiCalendarCheck size={64} className={styles.emptyIcon} />
      <p>Нет добавленных форм мероприятий</p>
      <Button onClick={openModal()}>Добавить первую форму</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка форм мероприятий...</span>
    </div>
  )

  const renderTable = () => (
    <div className={styles.tableContainer}>
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key !== 'actions' ? { cursor: 'pointer' } : {}) }}
                onClick={key !== 'actions' ? () => handleSort(key) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => {
            const isSelected = select?.acid === item.acid
            return (
              <tr
                key={item.acid}
                onClick={handleRowClick(item)}
                onDoubleClick={handleRowDoubleClick(item)}
                className={`${styles.tableRow} ${isSelected ? styles.selectedRow : ''}`}
              >
                <td>
                  <span className={styles.itemName}>{item.name}</span>
                </td>
                <td className={styles.checkboxCell}>
                  <Checkbox checked={!!item.closed} disabled />
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={handleDelete(item.acid)}
                    title="Удалить форму мероприятия"
                    aria-label={`Удалить ${item.name}`}
                  >
                    <BiTrash size={20} />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить</Button>
        {isSelectionMode && select?.acid && (
          <Button onClick={handleConfirmSelection}>Выбрать</Button>
        )}
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper} style={isSelectionMode ? { padding: 0 } : {}}>
      <header className={styles.header}>
        <h1>Формы мероприятий</h1>
        {isSelectionMode && (
          <p className={styles.subtitle}>Выберите форму мероприятия из списка</p>
        )}
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <ActivityTypeModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title="Уверены что хотите удалить форму мероприятия?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}

export default ActivityTypes
